import { ArrowLeft } from "lucide-react";
import { Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

interface Props {
  /** Fallback destination if there's no router history (e.g. direct landing). */
  fallback?: { to: "/" | "/discoveries" | "/categories" | "/collections" | "/play" | "/explore" | "/trending" };
  label?: string;
  className?: string;
}

/**
 * Compact back button. Uses browser history when available, otherwise
 * navigates to the provided fallback route so users are never stranded.
 */
export function BackButton({ fallback = { to: "/" }, label = "Back", className = "" }: Props) {
  const [lang] = useLocale();
  const localizedLabel = curatedUiTranslation(lang, label) ?? label;
  const router = useRouter();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    // history.length > 1 means there's somewhere to return to.
    setCanGoBack(typeof window !== "undefined" && window.history.length > 1);
  }, []);

  const cls = `inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground transition hover:border-primary/50 hover:text-primary ${className}`;

  if (canGoBack) {
    return (
      <button
        type="button"
        onClick={() => router.history.back()}
        className={cls}
        aria-label={localizedLabel}
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {localizedLabel}
      </button>
    );
  }
  return (
    <Link to={fallback.to} className={cls} aria-label={localizedLabel}>
      <ArrowLeft className="h-3.5 w-3.5" /> {localizedLabel}
    </Link>
  );
}
